@if (!isLoading && !error && product) {
  <div class="product-page" dir="rtl">
    <!-- SCRUM-222: only the owner / an admin can open a non-public ad. -->
    @if (pendingStatusLabel) {
      <div class="ad-private-notice" role="status">
        <strong>{{ pendingStatusLabel }}</strong>
        · המודעה אינה מוצגת לציבור - רק את/ה ומנהלי האתר רואים אותה.
      </div>
    }
    <!-- Breadcrumb -->
    <div class="breadcrumb">
      <a (click)="goBack()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        דף הבית
      </a>
      <span class="sep">»</span>
      <a>{{ getCategoryLabel() }}</a>
      <span class="sep">»</span>
      <span class="current">{{ product.name }}</span>
    </div>
    <!-- Main Layout -->
    <div class="product-layout">
      <!-- COL 1: Gallery -->
      <div class="col-gallery">
        <!-- Main Stage -->
        <div class="main-img-wrap">
          <!-- Image Stage -->
          @if (activeMediaType === 'image') {
            <img
              class="main-img"
              [src]="product.images?.[activeImageIndex]?.image || 'https://placehold.co/500x400/f5f5f5/999?text=📷'"
              [alt]="product.name"
              (error)="onImageError($event)"
              />
          }
          <!-- Video Stage -->
          @if (activeMediaType === 'video') {
            <video
              class="main-video"
              [src]="product.video_url"
              controls
              autoplay
              playsinline
              preload="metadata"
              (error)="onVideoError($event)"
            ></video>
          }
          <!-- Nav Arrows -->
          @if (getTotalMediaCount() > 1) {
            <button class="img-nav prev" (click)="prevImage()">‹</button>
          }
          @if (getTotalMediaCount() > 1) {
            <button class="img-nav next" (click)="nextImage()">›</button>
          }
        </div>
        <!-- Thumbnails -->
        @if (getTotalMediaCount() > 1) {
          <div class="thumb-list">
            <!-- Image thumbs -->
            @for (img of product.images; track img; let i = $index) {
              <div
                class="thumb"
                [class.active]="activeMediaType === 'image' && i === activeImageIndex"
                (click)="setImage(i)"
                >
                <img [src]="img.image" [alt]="product.name" (error)="onImageError($event)" />
              </div>
            }
            <!-- Video thumb -->
            @if (product.video_url) {
              <div
                class="thumb thumb-video"
                [class.active]="activeMediaType === 'video'"
                (click)="setVideo()"
                >
                <video [src]="product.video_url" preload="metadata" muted></video>
                <div class="play-overlay">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                </div>
              </div>
            }
          </div>
        }
        <!-- Seller profile sits under the gallery -->
        <div class="seller-mini">
          <!-- SCRUM-87/88 — the avatar doubles as the entry point to the
               seller page, and carries the follow control. -->
          <a class="seller-avatar-wrap"
             [routerLink]="sellerId ? ['/seller', sellerId] : null"
             [class.is-link]="!!sellerId">
            <div class="seller-avatar">
              @if (product.User?.avatar) {
                <img [src]="product.User.avatar" alt="תמונת פרופיל של המפרסם" (error)="onImageError($event)" />
              }
              @if (!product.User?.avatar) {
                <span>{{ getSellerName().charAt(0) }}</span>
              }
            </div>
            <!-- Verified is IDENTITY and shows to everyone, including the owner.
                 Follow is an ACTION and only makes sense on someone else's ad, so
                 the two live on opposite corners instead of competing for one. -->
            @if (sellerIsVerified) {
              <span class="seller-verified" [attr.title]="sellerVerifiedText" [attr.aria-label]="sellerVerifiedText">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="currentColor" d="M12 1.5l2.6 2.1 3.3-.4 1 3.2 2.9 1.7-1.3 3.1 1.3 3.1-2.9 1.7-1 3.2-3.3-.4L12 22.5l-2.6-2.1-3.3.4-1-3.2L2.2 16l1.3-3.1L2.2 9.8l2.9-1.7 1-3.2 3.3.4L12 1.5z"/>
                  <path fill="#fff" d="M10.6 15.2l-2.9-2.9 1.3-1.3 1.6 1.6 4-4 1.3 1.3-5.3 5.3z"/>
                </svg>
              </span>
            }
            @if (sellerId && !isOwnAd) {
              <button type="button"
                      class="seller-follow"
                      [class.seller-follow--on]="isFollowingSeller"
                      [disabled]="sellerFollowBusy"
                      [attr.aria-label]="isFollowingSeller ? 'הפסק לעקוב' : 'עקוב אחרי המפרסם'"
                      [attr.title]="isFollowingSeller ? 'הפסק לעקוב' : 'עקוב אחרי המפרסם'"
                      (click)="toggleSellerFollow($event)">
                {{ sellerFollowBusy ? '·' : (isFollowingSeller ? '✓' : '+') }}
              </button>
            }
          </a>
          <div class="seller-text">
            <a class="seller-name"
               [routerLink]="sellerId ? ['/seller', sellerId] : null">{{ sellerHeadline }}</a>
            <span class="seller-role">
              @if (businessTag) { <span class="seller-biz-tag">{{ businessTag }}</span> }
              {{ sellerSubtitle || product.sellerType?.label || product.CurrentOwner?.Type || product.serviceType?.label || (businessTag ? '' : 'מפרסם פרטי') }}
            </span>
            @if (sellerIsVerified) {
              <span class="seller-license"><i class="mdi mdi-verified"></i> {{ sellerVerifiedText }}@if (sellerLicenseNumber) { · {{ sellerLicenseNumber }} }</span>
            }
            @if (!sellerIsVerified && sellerLicenseNumber) {
              <span class="seller-license seller-license--pending">רישיון {{ sellerLicenseNumber }} · טרם אומת</span>
            }
            @if (isSponsored) { <span class="seller-sponsored">ממומן</span> }
          </div>
          @if (sellerFollowError) {
            <span class="seller-follow-err">{{ sellerFollowError }}</span>
          }
        </div>
      </div>
      <!-- COL 2: Product Info -->
      <div class="col-info">
        <h1 class="product-title" dir="rtl">{{ product.name }}</h1>
        <div class="info-line"><span class="info-label">קטגוריה:</span><span class="info-value link">{{ getCategoryLabel() }}</span></div>
        @if (product.resourcetype === 'Car' && !hasCategoryDetails()) {
          <div class="info-line"><span class="info-label">מצב:</span><span class="info-value in-stock">{{ product.NumberOfOwner?.numberOnwer }}</span></div>
        }
        <!-- SCRUM-154: motorcycle / scooter -->
        @if (product.resourcetype === 'Car' && hasCategoryDetails() && getCategoryDetail('hand')) {
          <div class="info-line"><span class="info-label">מצב:</span><span class="info-value in-stock">{{ getCategoryDetail('hand') }}</span></div>
        }
        @if (product.resourcetype === 'Nadlan') {
          <div class="info-line"><span class="info-label">מצב:</span><span class="info-value in-stock">{{ product.StatusOfNadlan?.nameHe }}</span></div>
        }
        @if (product.resourcetype === 'Sale2') {
          <div class="info-line"><span class="info-label">מצב:</span><span class="info-value in-stock">{{ product.ProductStatus?.statusName }}</span></div>
        }
        @if (product.resourcetype === 'Pet') {
          <div class="info-line"><span class="info-label">סוג:</span><span class="info-value in-stock">{{ product.listingType?.label }}</span></div>
        }
        @if (product.resourcetype === 'Required') {
          <div class="info-line"><span class="info-label">סוג משרה:</span><span class="info-value in-stock">{{ product.jobType?.label }}</span></div>
        }
        @if (product.resourcetype === 'Coworker') {
          <div class="info-line"><span class="info-label">זמינות:</span><span class="info-value in-stock">{{ product.availability?.label }}</span></div>
        }
        @if (product.resourcetype === 'Teacher') {
          <div class="info-line"><span class="info-label">השכלה:</span><span class="info-value in-stock">{{ product.teacherEducation?.label }}</span></div>
        }
        <!-- Rating summary -->
        @if (topLevelReviews().length > 0) {
          <div class="rating-row">
            <span class="stars">
              @for (s of [1,2,3,4,5]; track s) {
                <span class="star" [class.filled]="s <= averageRating">★</span>
              }
            </span>
            <span class="rating-count">{{ topLevelReviews().length }} דירוגים</span>
          </div>
        }
        <!-- SCRUM-119: ad impression count -->
        @if (product.impressions_count !== undefined && product.impressions_count !== null) {
          <div class="views-row">
            <i class="fa fa-eye" aria-hidden="true"></i>
            <span class="views-count">{{ product.impressions_count }} צפיות</span>
          </div>
        }
        @if (product.description) {
          <p class="short-desc" style="white-space: pre-line; text-align: justify">{{ getDescription() }}</p>
        }
        <div class="price-block">
          @if (isMarketProduct() && getMarketPrice()) {
            <span class="price">{{ getMarketPrice() }}</span>
          } @else {
            <span class="price">{{ getDisplayPrice() }}</span>
          }
          @if (product.priceNegotiable || product.isNegotiable) {
            <span class="negotiable-badge">מחיר גמיש</span>
          }
          @if (isMarketProduct()) {
            <span class="market-badge">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
              מוצר למכירה
            </span>
          }
        </div>
        <!-- Quick Specs -->
        <div class="quick-specs">
          <!-- SCRUM-154: motorcycle / scooter quick specs -->
          @if (product.resourcetype === 'Car' && hasCategoryDetails()) {
            @for (detail of getCategoryQuickSpecs(); track detail.key) {
              <div class="qs-row"><span class="qs-label">{{ detail.label }}:</span><span class="qs-val">{{ detail.value }}</span></div>
            }
          }
          @if (product.resourcetype === 'Car' && !hasCategoryDetails()) {
            <div class="qs-row"><span class="qs-label">יצרן:</span><span class="qs-val">{{ product.CodeProducer }}</span></div>
            <div class="qs-row"><span class="qs-label">דגם:</span><span class="qs-val">{{ product.MainModel || '—' }}</span></div>
            <div class="qs-row"><span class="qs-label">שנה:</span><span class="qs-val">{{ product.year }}</span></div>
            <div class="qs-row"><span class="qs-label">ק״מ:</span><span class="qs-val">{{ formatMileage(product.mileage) }}</span></div>
            <div class="qs-row"><span class="qs-label">צבע:</span><span class="qs-val color-val"><span class="color-swatch" [style.background]="product.Color?.colorCode"></span>{{ product.Color?.colorName }}</span></div>
          }
          @if (product.resourcetype === 'Nadlan') {
            <div class="qs-row"><span class="qs-label">סוג נכס:</span><span class="qs-val">{{ product.TypeOfNadlan?.nameHe }}</span></div>
            <div class="qs-row"><span class="qs-label">חדרים:</span><span class="qs-val">{{ product.RoomsNum?.numberOfRooms }}</span></div>
            @if (product.Floor !== null) {
              <div class="qs-row"><span class="qs-label">קומה:</span><span class="qs-val">{{ product.Floor }}@if (product.TotalFloor) {
              <span> / {{ product.TotalFloor }}</span>
            }</span></div>
          }
          @if (product.buildingMeterTotal) {
            <div class="qs-row"><span class="qs-label">שטח:</span><span class="qs-val">{{ product.buildingMeterTotal }} מ״ר</span></div>
          }
          <div class="qs-row"><span class="qs-label">עיר:</span><span class="qs-val">{{ product.City?.nameHe }}</span></div>
        }
        @if (product.resourcetype === 'Pet') {
          <div class="qs-row"><span class="qs-label">גזע:</span><span class="qs-val">{{ product.breed?.label }}</span></div>
          <div class="qs-row"><span class="qs-label">גיל:</span><span class="qs-val">{{ product.age?.label }}</span></div>
          <div class="qs-row"><span class="qs-label">מין:</span><span class="qs-val">{{ product.gender?.label }}</span></div>
          <div class="qs-row"><span class="qs-label">גודל:</span><span class="qs-val">{{ product.size?.label }}</span></div>
          <div class="qs-row"><span class="qs-label">עיר:</span><span class="qs-val">{{ product.city?.nameHe }}</span></div>
        }
        @if (product.resourcetype === 'Sale2') {
          @if (product.subCategory) {
            <div class="qs-row"><span class="qs-label">תת קטגוריה:</span><span class="qs-val">{{ product.subCategory?.subCategoryName }}</span></div>
          }
          <div class="qs-row"><span class="qs-label">עיר:</span><span class="qs-val">{{ product.City?.nameHe }}</span></div>
        }
        @if (product.resourcetype === 'Required') {
          <div class="qs-row"><span class="qs-label">תחום:</span><span class="qs-val">{{ product.jobCategory?.label }}</span></div>
          <div class="qs-row"><span class="qs-label">מיקום:</span><span class="qs-val">{{ product.workLocationType?.label }}</span></div>
          <div class="qs-row"><span class="qs-label">חברה:</span><span class="qs-val">{{ product.companyName }}</span></div>
          <div class="qs-row"><span class="qs-label">עיר:</span><span class="qs-val">{{ product.city?.nameHe }}</span></div>
        }
        @if (product.resourcetype === 'Coworker') {
          <div class="qs-row"><span class="qs-label">תחום:</span><span class="qs-val">{{ product.professionCategory?.label }}</span></div>
          <div class="qs-row"><span class="qs-label">ניסיון:</span><span class="qs-val">{{ product.experienceYears?.label }}</span></div>
          <div class="qs-row"><span class="qs-label">עסק:</span><span class="qs-val">{{ product.businessName }}</span></div>
          <div class="qs-row"><span class="qs-label">עיר:</span><span class="qs-val">{{ product.city?.nameHe }}</span></div>
        }
        @if (product.resourcetype === 'Teacher') {
          <div class="qs-row"><span class="qs-label">ניסיון:</span><span class="qs-val">{{ product.experienceYears?.label }}</span></div>
          @if (product.pricePerPackage) {
            <div class="qs-row"><span class="qs-label">חבילה:</span><span class="qs-val">₪{{ product.pricePerPackage }} ({{ product.packageSize }} שיעורים)</span></div>
          }
          <div class="qs-row"><span class="qs-label">עיר:</span><span class="qs-val">{{ product.city?.nameHe }}</span></div>
          <div class="qs-row"><span class="qs-label">אזור:</span><span class="qs-val">{{ product.region?.label }}</span></div>
        }
      </div>
      <!-- Market: Cart + Buy Now buttons (SCRUM-20) -->
      @if (isMarketProduct()) {
        <div class="cart-row">
          @if (!isOutOfStock()) {
            <!-- Add to cart -->
            <button class="add-to-cart-btn"
              [class.cart-added]="cartAdded"
              (click)="addToCart()">
              @if (!cartAdded) {
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
              }
              @if (cartAdded) {
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              }
              <span>{{ cartAdded ? 'נוסף לסל!' : 'הוסף לסל' }}</span>
            </button>
            <!-- Buy now -->
            <button class="buy-now-btn"
              [class.loading]="cartLoading"
              [disabled]="cartLoading"
              (click)="buyNow()">
              @if (cartLoading) {
                <span class="btn-spinner"></span>
              }
              @if (!cartLoading) {
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              }
              <span>{{ cartLoading ? 'מעבד...' : 'קנה עכשיו' }}</span>
            </button>
            @if (cartError) {
              <p class="cart-error">{{ cartError }}</p>
            }
          } @else {
            <button class="out-of-stock-btn" disabled>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
              <span>אזל המלאי</span>
            </button>
          }
        </div>
      }
      <div class="action-row">
        <app-contact-menu [phone]="getPhone()" [itemName]="product.name" [itemId]="product.id" [accentColor]="'#1565c0'" menuAlign="right"
                          [businessContact]="businessContact" [businessLabel]="businessTag" [sellerId]="sellerId"></app-contact-menu>
        <!-- SCRUM-133: share menu (Facebook / Instagram / WhatsApp / copy link) -->
        <div class="share-wrap">
          <button class="share-btn" type="button" title="שתף" aria-label="שתף" aria-haspopup="menu"
                  [attr.aria-expanded]="shareOpen" [class.active]="shareOpen" (click)="toggleShare($event)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          </button>
          @if (shareOpen) {
            <div class="share-menu" role="menu" dir="rtl" (click)="$event.stopPropagation()">
              <button type="button" role="menuitem" class="share-item facebook" (click)="shareFacebook()">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 10-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0022 12z"/></svg>
                <span>פייסבוק</span>
              </button>
              <button type="button" role="menuitem" class="share-item instagram" (click)="shareInstagram()">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>
                <span>אינסטגרם</span>
              </button>
              <button type="button" role="menuitem" class="share-item whatsapp" (click)="shareWhatsApp()">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.26-.46-2.39-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.07c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.08-.13-.28-.2-.57-.35zM12 2C6.48 2 2 6.48 2 12c0 1.89.53 3.66 1.44 5.17L2 22l4.83-1.44A9.96 9.96 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2z"/></svg>
                <span>וואטסאפ</span>
              </button>
              <button type="button" role="menuitem" class="share-item copy" (click)="copyShareLink()">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                <span>העתקת קישור</span>
              </button>
            </div>
          }
        </div>
      </div>
    </div>
    <!-- COL 3: Sidebar -->
    <div class="col-sidebar">
      <!-- SCRUM-287 — "פרטי העסק", only when the business switched it on -->
      @if (businessContact) {
        <app-business-contact [contact]="businessContact" [label]="businessTag" [adId]="product.id"
                              [sellerId]="sellerId"></app-business-contact>
      }
      <div class="sidebar-card">
        <div class="sb-icon blue"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg></div>
        <div class="sb-text"><strong>יצירת קשר</strong><span>{{ getPhone() || 'צור קשר ישיר' }}</span></div>
      </div>
      <div class="sidebar-card">
        <div class="sb-icon green"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
        <div class="sb-text"><strong>מודעה מאומתת</strong><span>{{ product.adsRoute?.title || 'מודעה רגילה' }}</span></div>
      </div>
      <div class="sidebar-card">
        <div class="sb-icon orange"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
        <div class="sb-text"><strong>פורסם</strong><span>{{ getCreatedDate() }}</span></div>
      </div>
    </div>
  </div>
  <!-- Tabs Section -->
  <div class="tabs-section">
    <div class="tabs-header">
      <button class="tab" [class.active]="activeTab === 'desc'" (click)="activeTab = 'desc'">תיאור</button>
      <button class="tab" [class.active]="activeTab === 'details'" (click)="activeTab = 'details'">פרטים נוספים</button>
      <button class="tab" [class.active]="activeTab === 'features'" (click)="activeTab = 'features'">מאפיינים</button>
      <button class="tab" [class.active]="activeTab === 'reviews'" (click)="activeTab = 'reviews'">
        ביקורות
        @if (topLevelReviews().length) {
          <span class="tab-badge">{{ topLevelReviews().length }}</span>
        }
      </button>
    </div>
    <div class="tab-body">
      <!-- Tab: Description -->
      @if (activeTab === 'desc') {
        <div class="tab-content">
          @if (product.description) {
            <p style="white-space: pre-line; text-align: justify">{{ getDescription() }}</p>
          }
          @if (product.FreeText) {
            <p>{{ product.FreeText }}</p>
          }
          @if (!product.description && !product.FreeText) {
            <p class="empty-tab">אין תיאור זמין</p>
          }
        </div>
      }
      <!-- Tab: Details -->
      @if (activeTab === 'details') {
        <div class="tab-content">
          <!-- SCRUM-135: only rows backed by real data; empty state otherwise -->
          @if (detailRows().length) {
            <table class="details-table">
              @for (row of detailRows(); track row.label) {
                <tr><td>{{ row.label }}</td><td>{{ row.value }}</td></tr>
              }
            </table>
          } @else {
            <p class="empty-tab">אין פרטים נוספים</p>
          }
        </div>
      }
      <!-- Tab: Features -->
      @if (activeTab === 'features') {
        <div class="tab-content">
          <!-- SCRUM-135: same rule as the details tab -->
          @if (featureChips().length) {
            <div class="features-wrap">
              @for (f of featureChips(); track $index) {
                <span class="feature" [class.accent]="f.accent">{{ f.text }}</span>
              }
            </div>
          } @else {
            <p class="empty-tab">אין מאפיינים</p>
          }
        </div>
      }
      <!-- Tab: Reviews -->
      @if (activeTab === 'reviews') {
        <div class="tab-content reviews-tab">
          <div class="reviews-layout">
            <!-- Rating Summary -->
            <div class="rating-summary">
              <div class="rating-big">
                <span class="big-number">{{ averageRating || 0 }}</span>
                <div class="big-stars">
                  @for (s of [1,2,3,4,5]; track s) {
                    <span class="star" [class.filled]="s <= averageRating">★</span>
                  }
                </div>
                <span class="total-reviews">מבוסס על {{ topLevelReviews().length }} ביקורות</span>
              </div>
              <div class="rating-bars">
                @for (count of ratingDistribution; track count; let i = $index) {
                  <div class="bar-row">
                    <span class="bar-label">{{ 5 - i }}</span>
                    <span class="bar-star">★</span>
                    <div class="bar-track">
                      <div class="bar-fill" [style.width.%]="getRatingPercent(4 - i)"></div>
                    </div>
                    <span class="bar-count">{{ ratingDistribution[4 - i] }}</span>
                  </div>
                }
              </div>
            </div>
            <!-- Reviews List -->
            <div class="reviews-list">
              @for (r of topLevelReviews(); track r.id || $index) {
                <div class="review-card thread-review-card">
                  <app-trend-comment-thread [comments]="reviews" [comment]="r" [postId]="product.id" [replyTarget]="replyTarget" [replyText]="replyText" [replySubmitting]="replySubmitting" variant="review" (replyRequested)="startReviewReply($event)" (replyTextChange)="replyText = $event" (replySubmitted)="submitReviewReply()" (replyCancelled)="cancelReviewReply()" (reactionRequested)="reactToReview($event)"></app-trend-comment-thread>
                </div>
              }
              @if (topLevelReviews().length === 0 && !reviewsLoading) {
                <div class="no-reviews">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                  <p>אין ביקורות עדיין. היה הראשון לכתוב ביקורת!</p>
                </div>
              }
              @if (reviewsLoading) {
                <div class="reviews-loader">
                  <div class="loader"><span></span><span></span><span></span></div>
                </div>
              }
            </div>
            <!-- Write Review Form -->
            <div class="review-form">
              <h3>כתוב ביקורת</h3>
              @if (reviewSubmitted) {
                <div class="success-msg">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  הביקורת נשלחה בהצלחה!
                </div>
              }
              <div class="form-group">
                <label>דירוג</label>
                <div class="star-picker">
                  @for (s of [1,2,3,4,5]; track s) {
                    <span
                      class="pick-star"
                      [class.active]="s <= (hoverRating || newReview.rating)"
                      (click)="setNewRating(s)"
                      (mouseenter)="setHoverRating(s)"
                      (mouseleave)="clearHoverRating()"
                    >★</span>
                  }
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>שם</label>
                  <input type="text" [(ngModel)]="newReview.reviewer" placeholder="השם שלך" />
                </div>
                <div class="form-group">
                  <label>אימייל</label>
                  <input type="email" [(ngModel)]="newReview.reviewer_email" placeholder="האימייל שלך" />
                </div>
              </div>
              <div class="form-group">
                <label>ביקורת</label>
                <textarea [(ngModel)]="newReview.review" placeholder="שתף את החוויה שלך..." rows="4"></textarea>
              </div>
              <button
                class="submit-review-btn"
                (click)="submitReview()"
                [disabled]="submittingReview || !newReview.reviewer || !newReview.review || newReview.rating === 0"
                >
                @if (!submittingReview) {
                  <span>שלח ביקורת</span>
                }
                @if (submittingReview) {
                  <span>שולח...</span>
                }
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
</div>
}

<!-- ── Related Products ───────────────────────────────────────── -->
@if (relatedProducts.length > 0) {
  <section class="related-section" dir="rtl">
    <div class="related-container">
      <div class="related-header">
        <span class="related-badge">{{ relatedProducts.length }} {{ 'sale4you.label.related.badge' | translate }}</span>
        <div>
          <h5 class="related-title">{{ 'sale4you.label.related.title' | translate }}</h5>
          <p class="related-subtitle">{{ 'sale4you.label.related.subtitle' | translate }}</p>
        </div>
      </div>
      <div class="related-slider-wrap">
        <button class="related-nav-btn related-nav-btn--right" (click)="scrollRelated('right')" aria-label="הקודם">&#8249;</button>
        <div class="related-scroll-wrap"
          #relatedTrack
          (touchstart)="onRelatedTouchStart($event)"
          (touchend)="onRelatedTouchEnd($event)">
          <div class="related-track">
            @for (p of relatedProducts; track p) {
              <a class="related-card" [routerLink]="['/single', p.id]">
                <div class="related-img-wrap">
                  <img [src]="p.images?.[0]?.image || 'https://placehold.co/200x140/f5f5f5/999?text=📷'"
                    [alt]="p.name" class="related-img" (error)="onImageError($event)" />
                  <span class="related-category-tag">{{ getCategoryLabel() }}</span>
                </div>
                <div class="related-info">
                  <p class="related-name">{{ p.name }}</p>
                  @if (p.area) {
                    <p class="related-location">📍 {{ p.area }}</p>
                  }
                  <div class="related-footer">
                    @if (p.price) {
                      <span class="related-price">{{ p.price | number }} ₪</span>
                    }
                    @if (!p.price) {
                      <span class="related-price no-price">{{ 'sale4you.label.related.contact' | translate }}</span>
                    }
                    <span class="related-arrow">←</span>
                  </div>
                </div>
              </a>
            }
          </div>
        </div>
        <button class="related-nav-btn related-nav-btn--left" (click)="scrollRelated('left')" aria-label="הבא">&#8250;</button>
      </div>
    </div>
  </section>
}

<!-- Loading / Error -->
@if (isLoading) {
  <div class="state-screen" dir="rtl">
    <div class="loader"><span></span><span></span><span></span></div>
    <p>טוען פרטים...</p>
  </div>
}
@if (error && !isLoading) {
  <div class="state-screen" dir="rtl">
    @if (notAvailable) {
      <p>המודעה אינה זמינה</p>
      <a class="retry-btn" routerLink="/">לדף הבית</a>
    } @else {
      <p>אופס! משהו השתבש</p>
      <button class="retry-btn" (click)="loadProduct(product?.id)">נסה שוב</button>
    }
  </div>
}
